import { useState, useEffect, useRef } from "react";
import { Modal } from "../../ui/modal";
import Alert from "../../ui/alert/Alert";
import { Transaction, Wallet } from "../types";
import { X, Search, Loader2, CheckCircle, Building, User } from "lucide-react";
import axios from "axios";
import { cache } from "../../../utils";
import { User as UserType } from "../../../stores/useUserStore";
import EnterPinModal from "../../ui/modal/EnterPinModal";
import Spinner from "../../ui/Spinner/Spinner";
import Toast from "../../ui/toast/Toast";
import { autoClearError } from "../../../utils/helpers";

interface BankTransferWithdrawModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (transaction?: Transaction) => void;
  wallet: Wallet;
  amount: number;
  fee: number;
  preloadedBanks?: any[]; // Banks passed from parent
}

const BankTransferWithdrawModal = ({
  isOpen,
  onClose,
  wallet,
  amount,
  fee,
  onSuccess,
  preloadedBanks = [],
}: BankTransferWithdrawModalProps) => {
  const [loading, setLoading] = useState(false); // Only for withdrawal processing
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [banks, setBanks] = useState<any[]>(preloadedBanks);
  const [filteredBanks, setFilteredBanks] = useState<any[]>([]);
  const [bankSearchQuery, setBankSearchQuery] = useState("");
  const [showBankDropdown, setShowBankDropdown] = useState(false);
  const [isVerifyingAccount, setIsVerifyingAccount] = useState(false);
  const [accountNameVerified, setAccountNameVerified] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  
  const [details, setDetails] = useState({
    bankCode: "",
    providerName: "",
    accountNumber: "",
    accountName: "",
    narration: "",
  });

  const bankSearchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      resetForm();
      // If we have preloaded banks, use them
      if (preloadedBanks.length > 0) {
        setBanks(preloadedBanks);
      }
    }
  }, [isOpen, preloadedBanks]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (bankSearchRef.current && !bankSearchRef.current.contains(event.target as Node)) {
        setShowBankDropdown(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Real-time bank filtering
  useEffect(() => {
    if (bankSearchQuery && banks.length > 0) {
      const filtered = banks
        .filter((bank) =>
          bank.name.toLowerCase().includes(bankSearchQuery.toLowerCase())
        )
        .slice(0, 10);
      setFilteredBanks(filtered);
    } else {
      setFilteredBanks([]);
    }
  }, [bankSearchQuery, banks]);

  // Account verification with debounce
  useEffect(() => {
    if (
      details.bankCode &&
      details.accountNumber.length === 10 &&
      !accountNameVerified &&
      !isVerifyingAccount
    ) {
      const debounceTimer = setTimeout(() => {
        verifyBankAccount();
      }, 1000);

      return () => clearTimeout(debounceTimer);
    }
  }, [details.bankCode, details.accountNumber]);

  const resetForm = () => {
    setDetails({
      bankCode: "",
      providerName: "",
      accountNumber: "",
      accountName: "",
      narration: "",
    });
    setBankSearchQuery("");
    setAccountNameVerified(false);
    setError("");
    setSuccess("");
    setShowBankDropdown(false);
  };

  const handleBankSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBankSearchQuery(value);
    
    // Show dropdown when typing
    if (value.trim()) {
      setShowBankDropdown(true);
    } else {
      setShowBankDropdown(false);
    }
  };

  const handleBankSelect = (bank: any) => {
    setDetails(prev => ({
      ...prev,
      bankCode: bank.code,
      providerName: bank.name,
      accountName: "",
      accountNumber: "",
    }));
    setShowBankDropdown(false);
    setBankSearchQuery(bank.name);
    setAccountNameVerified(false);
  };

  const handleRemoveSelectedBank = () => {
    setDetails(prev => ({
      ...prev,
      bankCode: "",
      providerName: "",
      accountName: "",
    }));
    setBankSearchQuery("");
    setAccountNameVerified(false);
    setError("");
  };

  const verifyBankAccount = async () => {
    if (!details.bankCode || !details.accountNumber) return;

    setIsVerifyingAccount(true);
    setError("");

    if (wallet.bankDetails?.accountNumber === details.accountNumber) {
      setError("You cannot withdraw to the same account linked to this wallet");
      setIsVerifyingAccount(false);
      autoClearError(setError);
      return;
    }

    try {
      const user = cache.get("USER") as UserType | null;
      const token = user?.token;
      
      const response = await axios.post(
        `${import.meta.env.VITE_BASE_URL}/transaction/verify-bank-account`,
        {
          currency: wallet.currency,
          bankCode: details.bankCode,
          accountNumber: details.accountNumber,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.success) {
        setAccountNameVerified(true);
        setDetails(prev => ({
          ...prev,
          accountName: response.data.data.accountName || "Verified Account",
        }));
      } else {
        setError("Account verification failed. Please check the details.");
      }
    } catch (error: any) {
      setError(error.response?.data?.message || "Failed to verify account details");
    } finally {
      setIsVerifyingAccount(false);
    }
  };

  const validateForm = () => {
    if (!details.bankCode) {
      setError("Please select a bank");
      return false;
    }
    
    if (!details.accountNumber || details.accountNumber.length !== 10) {
      setError("Please enter a valid 10-digit account number");
      return false;
    }
    
    if (!details.accountName || details.accountName.trim() === "") {
      setError("Account name is required");
      return false;
    }
    
    return true;
  };

  const handleContinue = () => {
    if (!validateForm()) return;
    setShowPinModal(true);
  };

  const handlePinVerification = async (pin: string): Promise<boolean> => {
    try {
      const user = cache.get("USER") as UserType | null;
      const token = user?.token;
      
      const validationResponse = await axios.post(
        `${import.meta.env.VITE_BASE_URL}/user/pin/validate`,
        { pin },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (validationResponse.data?.success) {
        // PIN is valid, proceed with withdrawal
        await processWithdrawal();
        return true;
      } else {
        setError("Invalid PIN. Please try again.");
        autoClearError(setError);
        return false;
      }
    } catch (error: any) {
      setError(error.response?.data?.message || "Failed to verify PIN");
      autoClearError(setError);
      return false;
    }
  };

  const processWithdrawal = async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const user = cache.get("USER") as UserType | null;
      const token = user?.token;

      const idempotencyKey = `WITHDRAWAL::BANKTRANSFER::${wallet.name}-${amount}-${details.accountNumber}`;
      
      const response = await axios.post(
        `${import.meta.env.VITE_BASE_URL}/transaction/transfer/bank`,
        {
          currency: wallet.currency,
          amount: amount,
          narration: details.narration || `Withdrawal from ${wallet.name} wallet`,
          accountNumber: details.accountNumber,
          bankCode: details.bankCode,
          accountName: details.accountName,
          bankName: details.providerName,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "idempotencykey": idempotencyKey,
          },
        }
      );

      if (response.data?.success) {
        const transaction = response.data.data as Transaction;
        setSuccess("Withdrawal successful!");
        setShowPinModal(false);
        setTimeout(() => {
          onSuccess(transaction);
        }, 3000);
      } else {
        throw new Error(response.data?.message || "Withdrawal failed");
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to process withdrawal");
      setSuccess("");
    } finally {
      setLoading(false);
    }
  };

  const handleAccountNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "");
    const limitedValue = value.slice(0, 10);
    
    setDetails(prev => ({ 
      ...prev, 
      accountNumber: limitedValue 
    }));
    
    // Clear verified account name when account number changes
    if (accountNameVerified && limitedValue !== details.accountNumber) {
      setAccountNameVerified(false);
      setError("");
      setDetails(prev => ({ 
        ...prev, 
        accountName: "" 
      }));
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        className="max-w-md rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900"
      >
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <button
              onClick={onClose}
              className="rounded-lg p-1 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {success && (
            <div className="mb-4">
              <Toast
                variant="success"
                title={success}
                message="Your account has been debited"
              />
            </div>
          )}
          
          <div className="mt-4 rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
            <div className="text-sm text-gray-500 dark:text-gray-400">
               <p className="text-sm text-gray-500 dark:text-gray-400">
                Transfer to {wallet.currency} bank account
              </p>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {wallet.symbol} {amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </div>
            <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Fee: {wallet.symbol} {fee.toFixed(2)} 
            </div>
          </div>
        </div>

        {error && !showPinModal && (
          <div className="mb-4">
            <Alert variant="error" title="Error" message={error} />
          </div>
        )}

        <div className="space-y-4">
          {/* Bank Selection - Now works immediately */}
          <div className="relative" ref={bankSearchRef}>
            <label className="mb-2 flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-300">
              <Building className="h-4 w-4" />
              Select Bank
            </label>
            
            {details.bankCode ? (
              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                    <Building className="h-4 w-4 text-primary" />
                  </div>
                  <span className="font-medium">{details.providerName}</span>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveSelectedBank}
                  className="rounded p-1 hover:bg-gray-200 dark:hover:bg-gray-700"
                  disabled={loading}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={bankSearchQuery}
                    onChange={handleBankSearchChange}
                    onFocus={() => {
                      if (bankSearchQuery && filteredBanks.length > 0) {
                        setShowBankDropdown(true);
                      }
                    }}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 pl-10 pr-4 py-3 text-gray-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white disabled:opacity-50"
                    placeholder="Search for your bank..."
                    disabled={loading}
                  />
                </div>
                
                {showBankDropdown && filteredBanks.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto dark:bg-gray-800 dark:border-gray-700">
                    {filteredBanks.map((bank) => (
                      <div
                        key={bank.code}
                        className="px-4 py-3 hover:bg-gray-100 cursor-pointer dark:hover:bg-gray-700"
                        onClick={() => handleBankSelect(bank)}
                      >
                        {bank.name}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Account Number */}
          <div>
            <label className="mb-2 flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-300">
              Account Number
              <span className="ml-auto text-xs text-gray-500">
                {details.accountNumber.length}/10
              </span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={details.accountNumber}
                onChange={handleAccountNumberChange}
                className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary dark:border-gray-700 dark:bg-gray-800 dark:text-white disabled:opacity-50"
                placeholder="Enter 10-digit account number"
                maxLength={10}
                minLength={10}
                disabled={loading || !details.bankCode}
                inputMode="numeric"
                pattern="[0-9]*"
              />
              {isVerifyingAccount && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Spinner className="h-4 w-4 text-blue-500" />
                </div>
              )}
              {accountNameVerified && !isVerifyingAccount && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                </div>
              )}
            </div>
          </div>

          {/* Account Name */}
          <div>
            <label className="mb-2 flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-300">
              <User className="h-4 w-4" />
              Account Name
            </label>
            <input
              type="text"
              value={details.accountName}
              onChange={(e) => setDetails(prev => ({ ...prev, accountName: e.target.value }))}
              className={`w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary dark:border-gray-700 dark:bg-gray-800 dark:text-white ${
                accountNameVerified ? "bg-green-50 dark:bg-green-900/20" : ""
              } disabled:opacity-50`}
              placeholder="Enter account holder name"
              disabled={accountNameVerified || loading}
            />
            {accountNameVerified && (
              <p className="mt-1 text-xs text-green-600 dark:text-green-400">
                Account name verified successfully
              </p>
            )}
          </div>

          {/* Narration */}
          <div>
            <label className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              Narration (Optional)
            </label>
            <input
              type="text"
              value={details.narration}
              onChange={(e) => setDetails(prev => ({ ...prev, narration: e.target.value }))}
              className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary dark:border-gray-700 dark:bg-gray-800 dark:text-white disabled:opacity-50"
              placeholder="Enter description for this transaction"
              maxLength={100}
              disabled={loading}
            />
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {details.narration.length}/100 characters
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          
          <button
            onClick={handleContinue}
            disabled={loading || !details.bankCode || !details.accountNumber || !details.accountName}
            className="rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
          >
            {loading ? (
              <>
                <Loader2 className="h-2 w-2 animate-spin" />
                
              </>
            ) : (
              "Continue"
            )}
          </button>
        </div>
      </Modal>

      {/* Separate PIN Modal */}
      <EnterPinModal
        isOpen={showPinModal}
        onClose={() => setShowPinModal(false)}
        onPinVerified={handlePinVerification}
        title="Confirm Withdrawal"
        description="Enter your 4-digit PIN to complete this withdrawal"
        showForgotPin={true}
      />
    </>
  );
};

export default BankTransferWithdrawModal;